import React, { useState, useEffect, useContext } from 'react'
import { Modal, Spinner } from 'reactstrap'
import { FaTimes, FaRegImage } from 'react-icons/fa'
import { IoCheckboxOutline } from 'react-icons/io5'
import InfiniteScroll from 'react-infinite-scroll-component'
import { SelectIdGroupContext } from '_context/SelectGroupContext'
import productsItemsApi from '_api/product'
import {
  AVAILABLE_STOCK_DEFAULT,
  LIMIT_PER_ORDER_DEFAULT,
  MINIMUM_ORDER_PER_PRODUCT,
} from '_utils/constant'
import useDebounce from '_hooks/useDebounce'
import { getCreateEventData } from '../../../../utils/localData'

const flattenProducts = (msgResp) => {
  const eventsArray = msgResp?.events || []
  if (!Array.isArray(eventsArray)) return []
  return eventsArray.flatMap((event) =>
    (event.products || []).map((product) => ({
      ...product,
      eventId: event.id,
      eventName: event.title,
      eventClosingTime: event.closingTimestamp,
      inStock: (product.maxQuantity || 0) - (product.orderCount || 0),
    })))
}

export default function ModalFromProduct({
  isOpen, toggle, closeModal, onAdd, groupId: groupIdProp,
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearch = useDebounce(searchQuery, 500)

  const context = useContext(SelectIdGroupContext)
  const createEventData = getCreateEventData()
  const groupId = groupIdProp || context?.infoGroupSelected?.groupId || createEventData?.groupId

  const [items, setItems] = useState([])
  const [hasMore, setHasMore] = useState(true)
  const [lastProductId, setLastProductId] = useState(null)
  const [lastProductTimestamp, setLastProductTimestamp] = useState(null)

  const [isFetchingInitial, setIsFetchingInitial] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSelectedIds([])
      setItems([])
      setHasMore(true)
      setLastProductId(null)
      setLastProductTimestamp(null)
    }
  }, [isOpen])

  // Fetch initial data (on mount/open or search change)
  useEffect(() => {
    if (!isOpen || !groupId) return

    const fetchInitialData = async () => {
      setIsFetchingInitial(true)
      const isSearching = debouncedSearch.trim() !== ''
      try {
        let response
        if (isSearching) {
          response = await productsItemsApi.searchProductsByHost({
            groupId,
            searchString: debouncedSearch.trim(),
          })
        } else {
          response = await productsItemsApi.getListProductsByHost({ groupId })
        }

        const data = response?.msgResp || {}
        const flatItems = flattenProducts(data)

        setItems(flatItems)
        setHasMore(data.hasMore ?? false)
        setLastProductId(data.lastProductId || null)
        setLastProductTimestamp(data.lastProductTimestamp || null)
      } catch (error) {
        console.error('Error fetching initial products:', error)
      } finally {
        setIsFetchingInitial(false)
      }
    }

    fetchInitialData()
  }, [isOpen, debouncedSearch, groupId])

  // Load more data when scrolling down
  const loadMoreItems = async () => {
    if (isFetchingInitial || isFetchingMore || !hasMore) return

    setIsFetchingMore(true)
    const isSearching = debouncedSearch.trim() !== ''
    try {
      let response
      if (isSearching) {
        response = await productsItemsApi.searchProductsByHost({
          groupId,
          searchString: debouncedSearch.trim(),
          lastProductId,
          lastProductTimestamp,
        })
      } else {
        response = await productsItemsApi.getListProductsByHost({
          groupId,
          lastProductId,
          lastProductTimestamp,
        })
      }

      const data = response?.msgResp || {}
      const flatItems = flattenProducts(data)

      setItems((prev) => [...prev, ...flatItems])
      setHasMore(data.hasMore ?? false)
      setLastProductId(data.lastProductId || null)
      setLastProductTimestamp(data.lastProductTimestamp || null)
    } catch (error) {
      console.error('Error fetching more products:', error)
    } finally {
      setIsFetchingMore(false)
    }
  }

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const isExist = prev.includes(id)
      return isExist ? prev.filter((item) => item !== id) : [...prev, id]
    })
  }

  const handleClose = () => {
    if (closeModal) {
      closeModal()
    } else if (toggle) {
      toggle()
    }
  }

  const handleAdd = () => {
    const selectedProducts = items.filter((p) => selectedIds.includes(p.id))
    const mappedSelectedItems = selectedProducts.map((item) => ({
      pid: '',
      name: item.name || '',
      description: item.description || null,
      price: item.price ?? 0,
      maxQuantity: item.maxQuantity ?? 1000,
      defaultQuantity: item.defaultQuantity ?? 0,
      limitPerOrder: item.limitPerOrder ?? 1000,
      minOrderQty: item.minOrderQty ?? 1,
      subItems: item.subItems || [],
      photoUrls: item.photoUrls || [],
      splitCasePurchase: item.splitCasePurchase || null,
    }))

    if (onAdd) {
      onAdd(mappedSelectedItems)
    }

    handleClose()
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} className='modal-from-product' centered>
      <div className='modal-from-product__container'>
        {/* Header */}
        <div className='modal-from-product__header'>
          <h3 className='modal-from-product__title'>Product Catalogue</h3>
          <button type='button' className='modal-from-product__close-btn' onClick={handleClose}>
            <FaTimes />
          </button>
        </div>
        {/* Search */}
        <div className='modal-from-product__search-wrapper'>
          <i className='modal-from-product__search-icon icon-search'></i>
          <input
            type='text'
            className='modal-from-product__search-input'
            placeholder='Search products...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {isFetchingInitial && (
            <Spinner
              className='modal-from-product__search-spinner'
              style={{ color: '#5BBCC5' }}
              size='sm'
            />
          )}
        </div>
        {/* List of Products */}
        <div id='scrollableDiv' className='modal-from-product__list'>
          {isFetchingInitial && items.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
              <Spinner style={{ color: '#5BBCC5' }} />
            </div>
          ) : items.length === 0 ? (
            <div className='modal-from-product__empty'>No products found</div>
          ) : (
            <InfiniteScroll
              dataLength={items.length}
              next={loadMoreItems}
              hasMore={hasMore}
              loader={
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px' }}>
                  <Spinner style={{ color: '#5BBCC5' }} size='sm' />
                  <span style={{ marginLeft: '8px', color: '#6b7280', fontSize: '13px', fontWeight: '500' }}>Loading...</span>
                </div>
              }
              scrollableTarget='scrollableDiv'
            >
              {items.map((product) => {
                const isSelected = selectedIds.includes(product.id)
                return (
                  <div
                    key={product.id}
                    className={`modal-from-product__item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleToggleSelect(product.id)}
                  >
                    <div className='modal-from-product__top-row'>
                      <div className='modal-from-product__img-box'>
                        {product.photoUrls && product.photoUrls.length > 0 ? (
                          <img
                            src={product.photoUrls[0]}
                            alt={product.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ) : (
                          <FaRegImage />
                        )}
                      </div>
                      <div className='modal-from-product__info'>
                        <h4 className='modal-from-product__item-name'>{product.name}</h4>
                        <p className='modal-from-product__item-desc'>{product.description}</p>
                      </div>
                      <div className='modal-from-product__price-box'>
                        <span className='modal-from-product__price'>
                          ${(product.price ?? 0).toFixed(2)}
                        </span>
                        <div className={`modal-from-product__checkbox ${isSelected ? 'checked' : ''}`}>
                          {isSelected && <div className='modal-from-product__checkbox-checked' />}
                        </div>
                      </div>
                    </div>

                    {(product.limitPerOrder !== LIMIT_PER_ORDER_DEFAULT ||
                      product.maxQuantity !== AVAILABLE_STOCK_DEFAULT ||
                      product.minOrderQty !== MINIMUM_ORDER_PER_PRODUCT ||
                      Object.keys(product?.splitCasePurchase || {}).length > 0 ||
                      product.subItems?.length > 0) && (
                      <div className='modal-from-product__checkbox-settings'>
                        {(product.limitPerOrder !== LIMIT_PER_ORDER_DEFAULT ||
                          product.maxQuantity !== AVAILABLE_STOCK_DEFAULT ||
                          product.minOrderQty !== MINIMUM_ORDER_PER_PRODUCT) && (
                          <div className='modal-from-product__checkbox-item'>
                            <IoCheckboxOutline /> <span>Order Config</span>
                          </div>
                        )}
                        {(Object.keys(product?.splitCasePurchase || {}).length > 0) && (
                          <div className='modal-from-product__checkbox-item'>
                            <IoCheckboxOutline /> <span>Split Case Purchase</span>
                          </div>
                        )}
                        {product.subItems?.length > 0 && (
                          <div className='modal-from-product__checkbox-item'>
                            <IoCheckboxOutline /> <span>Sub-Items</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </InfiniteScroll>
          )}
        </div>

        {/* Footer */}
        <div className='modal-from-product__footer'>
          <span className='modal-from-product__selected-count'>
            {selectedIds.length} selected
          </span>
          <div className='modal-from-product__actions'>
            <button
              type='button'
              className='modal-from-product__btn-cancel'
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type='button'
              className='modal-from-product__btn-submit'
              disabled={selectedIds.length === 0}
              onClick={handleAdd}
            >
              Add to Event
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
